<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type {
    EnergyContract,
    EnergyContractWritePayload,
    GazClasse,
    GazOptionTarifaire,
    GazStructureTarifaire,
} from "@modules/courtage/types"

// ================================
// TYPES
// ================================
type Props = {
    /** Company détentrice du contrat (scope multi-tenant). */
    idCompany: string
    /** Sources (compteurs gaz GRDF/Regaz) couvertes par le contrat. */
    idSources: string[]
    /** Contrat existant en mode édition (null = création). */
    modelValue?: EnergyContract | null
}

/** Une période en cours de saisie (Date côté form, sérialisée à la sortie). */
type PeriodeForm = {
    date_debut: Date | null
    date_fin: Date | null
    classe: GazClasse | null
    option_tarifaire: GazOptionTarifaire | null
    prix_kwh: number | null
    prix_abonnement: number | null
    cja: number | null
    car: number | null
}

const props = withDefaults(defineProps<Props>(), { modelValue: null })
const emit = defineEmits<{
    (e: "update:payload", payload: EnergyContractWritePayload): void
}>()

// ================================
// I18N
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            section_contrat: "Contrat",
            section_titulaire: "Titulaire",
            section_periodes: "Périodes tarifaires",
            nom: "Nom du contrat",
            nom_ph: "ex. Engie 2025 — Résidence Les Tilleuls",
            siret: "SIRET du fournisseur",
            raison_sociale_fournisseur: "Raison sociale du fournisseur",
            titulaire_type: "Type de titulaire",
            particulier: "Particulier (B2C)",
            professionnel: "Professionnel (B2B)",
            raison_sociale_titulaire: "Raison sociale du titulaire",
            nom_titulaire: "Nom du titulaire",
            periode: "Période",
            date_debut: "Début",
            date_fin: "Fin (vide = en cours)",
            classe: "Classe (ATRD)",
            option_tarifaire: "Option tarifaire",
            option_na: "— (T1/T2 : aucune)",
            prix_kwh: "Prix du kWh (HT, fourniture)",
            prix_abonnement: "Abonnement fournisseur (HT)",
            cja: "CJA (kWh/jour)",
            car: "CAR (kWh/an)",
            add_periode: "Ajouter une période",
            remove_periode: "Supprimer",
            help_periodes:
                "Une période par tranche tarifaire (ex. renégociation). La dernière période peut rester ouverte (sans date de fin).",
        },
    },
})

// ================================
// CONSTANTS
// ================================
const CLASSE_OPTIONS: { label: string; value: GazClasse }[] = [
    { label: "T1 (< 6 000 kWh/an)", value: "T1" },
    { label: "T2 (6 000 – 30 000)", value: "T2" },
    { label: "T3 (30 000 – 300 000)", value: "T3" },
    { label: "T4 (> 300 000)", value: "T4" },
]
const OPTION_TARIFAIRE_OPTIONS: { label: string; value: GazOptionTarifaire }[] =
    [
        { label: "B0", value: "B0" },
        { label: "B1", value: "B1" },
        { label: "B2I", value: "B2I" },
        { label: "B2S", value: "B2S" },
        { label: "B2M", value: "B2M" },
        { label: "TP", value: "TP" },
    ]

// ================================
// STATE
// ================================
const nom = ref("")
const siret = ref("")
const raisonSocialeFournisseur = ref("")
const isPersonneMorale = ref(false)
const raisonSocialeTitulaire = ref<string | null>(null)
const nomTitulaire = ref<string | null>(null)

const blankPeriode = (): PeriodeForm => ({
    date_debut: null,
    date_fin: null,
    classe: null,
    option_tarifaire: null,
    prix_kwh: null,
    prix_abonnement: null,
    cja: null,
    car: null,
})
const periodes = ref<PeriodeForm[]>([blankPeriode()])

// L'option tarifaire ne concerne que T3/T4.
const isOptionApplicable = (p: PeriodeForm) =>
    p.classe === "T3" || p.classe === "T4"

function addPeriode() {
    periodes.value.push(blankPeriode())
}
function removePeriode(index: number) {
    if (periodes.value.length > 1) periodes.value.splice(index, 1)
}

// ================================
// HYDRATATION (mode édition)
// ================================
watch(
    () => props.modelValue,
    (contract) => {
        if (!contract) return
        nom.value = contract.nom ?? ""
        siret.value = contract.fournisseur?.siret ?? ""
        raisonSocialeFournisseur.value =
            contract.fournisseur?.raison_sociale ?? ""
        const firstStruct = (contract.periodes_validite?.[0]
            ?.structure_tarifaire ?? {}) as Partial<GazStructureTarifaire>
        isPersonneMorale.value = Boolean(firstStruct.is_personne_morale)
        raisonSocialeTitulaire.value = firstStruct.raison_sociale ?? null
        nomTitulaire.value = firstStruct.nom_titulaire ?? null
        periodes.value = (contract.periodes_validite ?? []).map((p) => {
            const s = (p.structure_tarifaire ??
                {}) as Partial<GazStructureTarifaire>
            return {
                date_debut: p.date_debut ? new Date(p.date_debut) : null,
                date_fin: p.date_fin ? new Date(p.date_fin) : null,
                classe: s.classe ?? null,
                option_tarifaire: s.option_tarifaire ?? null,
                prix_kwh: s.prix_kwh ?? null,
                prix_abonnement: s.prix_abonnement ?? null,
                cja: s.cja ?? null,
                car: s.car ?? null,
            }
        })
        if (periodes.value.length === 0) periodes.value = [blankPeriode()]
    },
    { immediate: true }
)

// ================================
// VALIDATION
// ================================
const SIRET_RE = /^\d{14}$/
const isPeriodeValid = (p: PeriodeForm) =>
    p.date_debut !== null &&
    p.classe !== null &&
    typeof p.prix_kwh === "number" &&
    p.prix_kwh >= 0 &&
    typeof p.prix_abonnement === "number" &&
    p.prix_abonnement >= 0

const valid = computed(
    () =>
        nom.value.trim().length > 0 &&
        SIRET_RE.test(siret.value.trim()) &&
        raisonSocialeFournisseur.value.trim().length > 0 &&
        periodes.value.length > 0 &&
        periodes.value.every(isPeriodeValid)
)

// ================================
// PAYLOAD
// ================================
const toIso = (d: Date) => d.toISOString()
const toDateStr = (d: Date) => d.toISOString().slice(0, 10)

const payload = computed<EnergyContractWritePayload>(() => ({
    _id_company: props.idCompany,
    _id_sources: props.idSources,
    type_energie: "gaz",
    nom: nom.value.trim(),
    fournisseur: {
        siret: siret.value.trim(),
        raison_sociale: raisonSocialeFournisseur.value.trim(),
    },
    validation_status: "confirmed",
    periodes_validite: periodes.value.map((p) => {
        const structure: GazStructureTarifaire = {
            classe: p.classe as GazClasse,
            option_tarifaire: isOptionApplicable(p) ? p.option_tarifaire : null,
            cja: p.cja,
            car: p.car,
            fournisseur: raisonSocialeFournisseur.value.trim() || null,
            prix_kwh: p.prix_kwh as number,
            prix_abonnement: p.prix_abonnement as number,
            date_debut_contrat: p.date_debut ? toDateStr(p.date_debut) : null,
            date_fin_contrat: p.date_fin ? toDateStr(p.date_fin) : null,
            is_personne_morale: isPersonneMorale.value,
            raison_sociale: isPersonneMorale.value
                ? raisonSocialeTitulaire.value
                : null,
            nom_titulaire: isPersonneMorale.value ? null : nomTitulaire.value,
        }
        return {
            date_debut: p.date_debut ? toIso(p.date_debut) : "",
            date_fin: p.date_fin ? toIso(p.date_fin) : null,
            structure_tarifaire: structure as unknown as Record<
                string,
                unknown
            >,
        }
    }),
}))

watch(payload, (value) => emit("update:payload", value), {
    deep: true,
    immediate: true,
})

defineExpose({ valid })
</script>

<template>
    <div class="energy-contract-gaz-form flex flex-col gap-8">
        <!-- ===================== Contrat ===================== -->
        <section class="flex flex-col gap-4">
            <h3 class="text-primary font-medium">{{ t("section_contrat") }}</h3>
            <BaseInputWrapper input-id="ec-nom" :label="t('nom')" unfloat>
                <BaseInputText
                    id="ec-nom"
                    v-model="nom"
                    :placeholder="t('nom_ph')"
                    class="w-full"
                />
            </BaseInputWrapper>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <BaseInputWrapper
                    input-id="ec-siret"
                    :label="t('siret')"
                    unfloat
                >
                    <BaseInputText
                        id="ec-siret"
                        v-model="siret"
                        inputmode="numeric"
                        maxlength="14"
                        placeholder="14 chiffres"
                        class="w-full"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    input-id="ec-raison-fournisseur"
                    :label="t('raison_sociale_fournisseur')"
                    unfloat
                >
                    <BaseInputText
                        id="ec-raison-fournisseur"
                        v-model="raisonSocialeFournisseur"
                        placeholder="ex. ENGIE SA"
                        class="w-full"
                    />
                </BaseInputWrapper>
            </div>
        </section>

        <!-- ===================== Titulaire ===================== -->
        <section class="flex flex-col gap-4">
            <h3 class="text-primary font-medium">
                {{ t("section_titulaire") }}
            </h3>
            <BaseInputWrapper
                input-id="ec-titulaire-type"
                :label="t('titulaire_type')"
                unfloat
            >
                <BaseSelectButton
                    v-model="isPersonneMorale"
                    :allow-empty="false"
                    :options="[
                        { label: t('particulier'), value: false },
                        { label: t('professionnel'), value: true },
                    ]"
                    option-label="label"
                    option-value="value"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                v-if="isPersonneMorale"
                input-id="ec-raison-titulaire"
                :label="t('raison_sociale_titulaire')"
                unfloat
            >
                <BaseInputText
                    id="ec-raison-titulaire"
                    v-model="raisonSocialeTitulaire"
                    class="w-full"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                v-else
                input-id="ec-nom-titulaire"
                :label="t('nom_titulaire')"
                unfloat
            >
                <BaseInputText
                    id="ec-nom-titulaire"
                    v-model="nomTitulaire"
                    class="w-full"
                />
            </BaseInputWrapper>
        </section>

        <!-- ===================== Périodes ===================== -->
        <section class="flex flex-col gap-4">
            <div class="flex items-center justify-between">
                <h3 class="text-primary font-medium">
                    {{ t("section_periodes") }}
                </h3>
                <BaseButton
                    icon="pi pi-plus"
                    :label="t('add_periode')"
                    severity="secondary"
                    size="small"
                    text
                    @click="addPeriode"
                />
            </div>
            <p class="text-sm text-surface-500">{{ t("help_periodes") }}</p>

            <fieldset
                v-for="(periode, index) in periodes"
                :key="index"
                class="flex flex-col gap-4 rounded-lg border border-surface-200 p-4"
            >
                <div class="flex items-center justify-between">
                    <legend class="font-medium">
                        {{ t("periode") }} {{ index + 1 }}
                    </legend>
                    <BaseButton
                        v-if="periodes.length > 1"
                        icon="pi pi-trash"
                        :label="t('remove_periode')"
                        severity="danger"
                        size="small"
                        text
                        @click="removePeriode(index)"
                    />
                </div>

                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <BaseInputWrapper
                        :input-id="`ec-debut-${index}`"
                        :label="t('date_debut')"
                        unfloat
                    >
                        <BaseDatePicker
                            :input-id="`ec-debut-${index}`"
                            v-model="periode.date_debut"
                            date-format="dd/mm/yy"
                            show-icon
                            class="w-full"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :input-id="`ec-fin-${index}`"
                        :label="t('date_fin')"
                        unfloat
                    >
                        <BaseDatePicker
                            :input-id="`ec-fin-${index}`"
                            v-model="periode.date_fin"
                            date-format="dd/mm/yy"
                            show-icon
                            show-button-bar
                            class="w-full"
                        />
                    </BaseInputWrapper>
                </div>

                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <BaseInputWrapper
                        :input-id="`ec-classe-${index}`"
                        :label="t('classe')"
                        unfloat
                    >
                        <BaseSelect
                            :input-id="`ec-classe-${index}`"
                            v-model="periode.classe"
                            :options="CLASSE_OPTIONS"
                            option-label="label"
                            option-value="value"
                            placeholder="T1 / T2 / T3 / T4"
                            class="w-full"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :input-id="`ec-option-${index}`"
                        :label="t('option_tarifaire')"
                        unfloat
                    >
                        <BaseSelect
                            :input-id="`ec-option-${index}`"
                            v-model="periode.option_tarifaire"
                            :options="OPTION_TARIFAIRE_OPTIONS"
                            option-label="label"
                            option-value="value"
                            :disabled="!isOptionApplicable(periode)"
                            :placeholder="
                                isOptionApplicable(periode)
                                    ? 'B0 … TP'
                                    : t('option_na')
                            "
                            show-clear
                            class="w-full"
                        />
                    </BaseInputWrapper>
                </div>

                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <BaseInputWrapper
                        :input-id="`ec-prix-kwh-${index}`"
                        :label="t('prix_kwh')"
                        unfloat
                        suffix="€/kWh"
                    >
                        <BaseInputNumber
                            :input-id="`ec-prix-kwh-${index}`"
                            v-model="periode.prix_kwh"
                            mode="decimal"
                            :min="0"
                            :min-fraction-digits="0"
                            :max-fraction-digits="5"
                            class="w-full"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :input-id="`ec-prix-abo-${index}`"
                        :label="t('prix_abonnement')"
                        unfloat
                        suffix="€/an"
                    >
                        <BaseInputNumber
                            :input-id="`ec-prix-abo-${index}`"
                            v-model="periode.prix_abonnement"
                            mode="decimal"
                            :min="0"
                            :min-fraction-digits="0"
                            :max-fraction-digits="2"
                            class="w-full"
                        />
                    </BaseInputWrapper>
                </div>

                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <BaseInputWrapper
                        :input-id="`ec-cja-${index}`"
                        :label="t('cja')"
                        unfloat
                        suffix="kWh/j"
                    >
                        <BaseInputNumber
                            :input-id="`ec-cja-${index}`"
                            v-model="periode.cja"
                            mode="decimal"
                            :min="0"
                            :max-fraction-digits="2"
                            class="w-full"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :input-id="`ec-car-${index}`"
                        :label="t('car')"
                        unfloat
                        suffix="kWh/an"
                    >
                        <BaseInputNumber
                            :input-id="`ec-car-${index}`"
                            v-model="periode.car"
                            mode="decimal"
                            :min="0"
                            :max-fraction-digits="2"
                            class="w-full"
                        />
                    </BaseInputWrapper>
                </div>
            </fieldset>
        </section>
    </div>
</template>

<style scoped></style>
